import { useState, useId } from 'react';
import { Clock, Trash2, Users, ChevronDown } from 'lucide-react';
import { cn } from '../../../shared';
import EmployeeSelect from './EmployeeSelect';
import type { NewJoiner, G2KEntry } from '../services/onboardingApi';
import type { UserProfile } from '../services/userProfileService';

export function formatDate(iso: string): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// New-joiner picker
// ---------------------------------------------------------------------------

interface JoinerSelectProps {
  joiners: NewJoiner[];
  selected: NewJoiner | null;
  onChange: (j: NewJoiner | null) => void;
  usedIds: Set<string>;
}

export function JoinerSelect({ joiners, selected, onChange, usedIds }: JoinerSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const id = useId();

  const filtered = joiners
    .filter(j => !usedIds.has(j.id) || j.id === selected?.id)
    .filter(j =>
      !search ||
      j.name.toLowerCase().includes(search.toLowerCase()) ||
      j.department.toLowerCase().includes(search.toLowerCase()),
    );

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setOpen(o => !o)}
        className={cn(
          'w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors bg-white',
          open ? 'border-blue-400 ring-1 ring-blue-400/30' : 'border-zinc-200 hover:border-zinc-300',
        )}
      >
        {selected ? (
          <>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold shrink-0">
              {selected.name.charAt(0)}
            </span>
            <span className="flex-1 font-medium text-zinc-900 truncate">{selected.name}</span>
            {selected.department && (
              <span className="text-xs text-zinc-400 shrink-0">{selected.department}</span>
            )}
          </>
        ) : (
          <>
            <Users className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
            <span className="flex-1 text-zinc-400">Select new joiner…</span>
          </>
        )}
        <ChevronDown className={cn('h-3.5 w-3.5 text-zinc-400 transition-transform shrink-0', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-zinc-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-zinc-100">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter by name or department…"
              className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-sm placeholder:text-zinc-400 focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="text-center py-6 text-zinc-400 text-xs">No new joiners available</div>
            ) : (
              filtered.map(j => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => { onChange(j); setOpen(false); setSearch(''); }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 transition-colors text-left',
                    usedIds.has(j.id) && j.id !== selected?.id && 'opacity-40 pointer-events-none',
                  )}
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold shrink-0">
                    {j.name.charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate">{j.name}</div>
                    <div className="text-xs text-zinc-400 truncate">
                      {[j.position, j.department].filter(Boolean).join(' · ')}
                      {j.startDate && ` · Start: ${formatDate(j.startDate)}`}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A single FINNie row inside the builder
// ---------------------------------------------------------------------------

export interface FINNieRowItem {
  rowId: string;
  employee: UserProfile | null;
  duration: 15 | 30;
}

export function FINNieRow({
  row,
  selectedEmails,
  onChange,
  onRemove,
  canRemove,
}: {
  row: FINNieRowItem;
  selectedEmails: string[];
  onChange: (updated: FINNieRowItem) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <EmployeeSelect
          selected={row.employee}
          onChange={emp => onChange({ ...row, employee: emp })}
          placeholder="Search FINNies…"
          excludeEmails={selectedEmails.filter(e => e !== row.employee?.email)}
        />
      </div>

      <div className="flex gap-1 shrink-0">
        {([15, 30] as const).map(d => (
          <button
            key={d}
            type="button"
            onClick={() => onChange({ ...row, duration: d })}
            className={cn(
              'flex items-center gap-1 rounded-lg border px-2.5 py-2 text-xs font-semibold transition-colors',
              row.duration === d
                ? 'border-blue-500 bg-blue-500 text-white'
                : 'border-zinc-200 bg-white text-zinc-500 hover:border-zinc-300',
            )}
          >
            <Clock className="h-3 w-3" />
            {d}m
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onRemove}
        disabled={!canRemove}
        className={cn(
          'shrink-0 transition-colors',
          canRemove ? 'text-zinc-300 hover:text-red-500' : 'text-zinc-200 cursor-not-allowed',
        )}
        aria-label="Remove"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Queue row (read-only display)
// ---------------------------------------------------------------------------

export function QueueRow({ entry, onRemove }: { entry: G2KEntry; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-white px-4 py-3 shadow-sm">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold shrink-0">
        {entry.joiner.name.charAt(0)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-zinc-900 truncate">{entry.joiner.name}</div>
        <div className="text-xs text-zinc-400 truncate">{entry.joiner.position || entry.joiner.department || 'New joiner'}</div>
      </div>

      <div className="text-zinc-300 shrink-0">↔</div>

      {entry.employee.photoURL ? (
        <img src={entry.employee.photoURL} className="h-8 w-8 rounded-full shrink-0" referrerPolicy="no-referrer" alt="" />
      ) : (
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white shrink-0">
          {entry.employee.displayName.charAt(0)}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-zinc-900 truncate">{entry.employee.displayName}</div>
        <div className="text-xs text-zinc-400 truncate">{entry.employee.email}</div>
      </div>

      <div className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 shrink-0">
        <Clock className="h-3 w-3" />
        {entry.durationMinutes} min
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="text-zinc-300 hover:text-red-500 transition-colors shrink-0"
        aria-label="Remove"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}
